import { GRADER_STUDY_INDEX_SESSION_KEY } from '../constants/graderStudyNavigation';
import AuthService from '../services/AuthService';

/**
 * Same rules as StudiesList.handleRowClick for grader study rows.
 */
export function computeIsViewOnlyForGraderStudy(
  study: {
    assignees?: { curr_user_id?: number | string }[];
    status?: string;
    grader_id?: number | string;
  },
  currentUserId: number | string | null | undefined
): boolean {
  const assigneeUserIds = (study.assignees || []).map(a => a.curr_user_id).filter(id => id != null);
  let isAssignee =
    currentUserId != null && assigneeUserIds.some(id => Number(id) === Number(currentUserId));
  if (!isAssignee && study.grader_id != null && currentUserId != null) {
    isAssignee = Number(study.grader_id) === Number(currentUserId);
  }
  const statusKey = (study.status || '').toString().trim().toLowerCase();
  const isCompleted =
    statusKey === 'completed' || statusKey === 'complete' || statusKey === 'reviewed';

  if (isCompleted) {
    return true;
  }
  return currentUserId != null && !isAssignee;
}

/**
 * Synchronous variant for beforeunload and other handlers that cannot await.
 */
export function isLoggedInUserStudyGraderSync(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const viewOnlyFlag = sessionStorage.getItem('isViewOnly');
  if (viewOnlyFlag === 'true') {
    return false;
  }
  if (viewOnlyFlag === 'false') {
    return true;
  }

  let currentUserId: number | string | null =
    (window as { currentUserGraderId?: number | string }).currentUserGraderId ?? null;

  if (currentUserId == null) {
    try {
      const currentUser = AuthService.getInstance().getCurrentUser();
      currentUserId = currentUser?.id ?? null;
    } catch {
      // ignore
    }
  }

  if (currentUserId == null) {
    return false;
  }

  try {
    const apiResponse = (window as { annotationsData?: { data?: Array<Record<string, unknown>> } })
      .annotationsData;
    const studyRow = apiResponse?.data?.[0];

    if (studyRow) {
      const study = (studyRow.study ?? studyRow.study_info) as
        | Parameters<typeof computeIsViewOnlyForGraderStudy>[0]
        | undefined;

      if (study) {
        return !computeIsViewOnlyForGraderStudy(study, currentUserId);
      }

      const annotationRows = (studyRow.annotations as Array<{ grader_id?: number | string }>) ?? [];
      if (
        annotationRows.some(
          row => row?.grader_id != null && String(row.grader_id) === String(currentUserId)
        )
      ) {
        return true;
      }
    }

    const indexRaw = sessionStorage.getItem(GRADER_STUDY_INDEX_SESSION_KEY);
    if (indexRaw) {
      const indexRow = JSON.parse(indexRaw) as Parameters<typeof computeIsViewOnlyForGraderStudy>[0];
      if (indexRow && typeof indexRow === 'object') {
        return !computeIsViewOnlyForGraderStudy(indexRow, currentUserId);
      }
    }
  } catch {
    // ignore
  }

  return false;
}

/**
 * True when the logged-in user may edit/save annotations for the open study
 * (assignee grader, not review-only). Used before unsaved-navigation prompts.
 */
export async function isLoggedInUserStudyGrader(): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false;
  }

  const viewOnlyFlag = sessionStorage.getItem('isViewOnly');
  if (viewOnlyFlag === 'true') {
    return false;
  }
  if (viewOnlyFlag === 'false') {
    return true;
  }

  let currentUserId: number | string | null =
    (window as { currentUserGraderId?: number | string }).currentUserGraderId ?? null;

  if (currentUserId == null) {
    try {
      const { default: AuthService } = await import('../services/AuthService');
      const currentUser = AuthService.getInstance().getCurrentUser();
      currentUserId = currentUser?.id ?? null;
    } catch {
      // ignore
    }
  }

  if (currentUserId == null) {
    return false;
  }

  try {
    const apiResponse = (window as { annotationsData?: { data?: Array<Record<string, unknown>> } })
      .annotationsData;
    const studyRow = apiResponse?.data?.[0];

    if (studyRow) {
      const study = (studyRow.study ?? studyRow.study_info) as
        | Parameters<typeof computeIsViewOnlyForGraderStudy>[0]
        | undefined;

      if (study) {
        return !computeIsViewOnlyForGraderStudy(study, currentUserId);
      }

      const annotationRows = (studyRow.annotations as Array<{ grader_id?: number | string }>) ?? [];
      if (
        annotationRows.some(
          row => row?.grader_id != null && String(row.grader_id) === String(currentUserId)
        )
      ) {
        return true;
      }
    }

    const indexRaw = sessionStorage.getItem(GRADER_STUDY_INDEX_SESSION_KEY);
    if (indexRaw) {
      const indexRow = JSON.parse(indexRaw) as Parameters<typeof computeIsViewOnlyForGraderStudy>[0];
      if (indexRow && typeof indexRow === 'object') {
        return !computeIsViewOnlyForGraderStudy(indexRow, currentUserId);
      }
    }
  } catch {
    // ignore
  }

  return false;
}
